import { useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { Edit3, Users } from 'lucide-react'

interface CreateWatchlistFormProps {
  onSubmit: (name: string, description: string) => void
  selectedAccountsCount: number
  isLoading?: boolean
}

export function CreateWatchlistForm({ onSubmit, selectedAccountsCount, isLoading = false }: CreateWatchlistFormProps) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) return
    onSubmit(name.trim(), description.trim())
  }

  const canSubmit = name.trim() && selectedAccountsCount > 0

  return (
    <div>
      <Card className="border border-slate-200/30 dark:border-slate-800/30">
        <CardHeader className="pb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10 flex-shrink-0">
              <Edit3 className="h-4 w-4 text-primary" />
            </div>
            <div>
              <CardTitle className="text-lg font-semibold">Watchlist Details</CardTitle>
              <CardDescription className="text-sm mt-1">
                Name your watchlist and add a description
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Watchlist Name *</label>
              <Input
                placeholder="e.g., Tech Innovators, Beauty Creators..."
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full"
                disabled={isLoading}
              />
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">Description (optional)</label>
              <Textarea
                placeholder="Describe what this watchlist is for..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full min-h-[80px] resize-none"
                disabled={isLoading}
              />
            </div>

            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-xs">
                <Users className="h-3 w-3 mr-1" />
                {selectedAccountsCount} accounts selected
              </Badge>
            </div>

            <Button 
              type="submit"
              disabled={!canSubmit || isLoading}
              className="w-full sm:w-auto"
            >
              {isLoading ? 'Creating Watchlist...' : 'Create Watchlist'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  )
} 